import { useMemo } from 'react';

import {
  Card,
  ShadowDivider,
  InfoTooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import { getCurrencyPrecision } from '@cast/utils';

import { WhenProvider } from 'components/cluster';
import { Money } from 'components/money';
import { TourStep } from 'core/tour';
import { ComputeSpendAnomalyTooltip } from 'features/cluster/cost-report/compute-spend/_components/ComputeSpendAnomalyTooltip';
import { isStorageReportsSupported } from 'features/cluster/utils';
import { CostReportChartWithAnomalies } from 'features/cost-report/components/chart/CostReportChartWithAnomalies';
import { LargeMetricsBox } from 'features/cost-report/components/cost-over-time/LargeMetricsBox';
import { ReportSection } from 'features/cost-report/components/ReportSection';
import { useCostOverTime } from 'features/cost-report/hooks/useCostOverTime';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { useCostReportTimeRangeSelection } from 'features/cost-report/hooks/useCostReportTimeRangeSelection';
import {
  ComputeSpendChartData,
  ComputeSpendMode,
} from 'features/cost-report/types/costOverTime';
import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { computeSpendColorsMap, ComputeSpendType } from 'types/cost-report';
import { generateEmptyTimeSeries } from 'utils/data/generateEmptyTimeSeries';

import { ComputeSpendControls, useComputeSpendTooltip } from './_components';
import { useComputeSpendChart, useComputeSpendControls } from './_hooks';
import {
  processDailyCostChartData,
  processCumulativeCostChartData,
} from './_utils';
import { costOverTimeTourId, tourSteps } from '../tourSteps';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type ComputeSpendSectionProps = {
  showStorage?: boolean;
};

export const ComputeSpendSection = ({
  showStorage: _showStorage = true,
}: ComputeSpendSectionProps) => {
  const { cluster } = useCluster();
  const isStorageSupported = isStorageReportsSupported(cluster);
  const showStorage = isStorageSupported && _showStorage;

  const config = useComputeSpendChart(showStorage);
  const { chartMode } = useComputeSpendControls();
  const { isDefaultFilter, chartType, currentDateRange, timezone } =
    useCostReportContext();
  const { getRange } = useCostReportTimeRangeSelection();
  const {
    isCollecting,
    aggregatedMetrics,
    dynamicCopiesMap,
    current,
    projectEndOfDayCost,
    applyUptime,
    storageCostGranularity,
    anomalies = [],
  } = useCostOverTime();

  const chartData = useMemo(() => {
    const commonParams = {
      current,
      projectEndOfDayCost,
      applyUptime,
      storageCostGranularity,
      anomalies,
      chartType,
      timezone,
    };

    return chartMode === ComputeSpendMode.DAILY_COST
      ? processDailyCostChartData(commonParams)
      : processCumulativeCostChartData(commonParams);
  }, [
    chartMode,
    current,
    projectEndOfDayCost,
    applyUptime,
    storageCostGranularity,
    anomalies,
    chartType,
    timezone,
  ]);

  const { data } = chartData;

  const emptyData = useMemo(
    () => generateEmptyTimeSeries({ range: currentDateRange }),
    [currentDateRange]
  );

  const LOADING_MONEY = '$--.--';

  const computeCost = aggregatedMetrics.computeCost;

  const mainCost = isDefaultFilter
    ? computeCost.todayProjected
    : computeCost.rangeSoFar;

  const onDemandCost = isDefaultFilter
    ? computeCost.onDemandProjected
    : computeCost.onDemandRangeSoFar;

  const fallbackCost = isDefaultFilter
    ? computeCost.fallbackProjected
    : computeCost.fallbackRangeSoFar;

  const spotCost = isDefaultFilter
    ? computeCost.spotProjected
    : computeCost.spotRangeSoFar;

  const storageCost = isDefaultFilter
    ? computeCost.storageProjected
    : computeCost.storageRangeSoFar;

  const mainCostPrecision = getCurrencyPrecision(mainCost);
  const onDemandCostPrecision = getCurrencyPrecision(onDemandCost);
  const fallbackCostPrecision = getCurrencyPrecision(fallbackCost);
  const spotCostPrecision = getCurrencyPrecision(spotCost);
  const storageCostPrecision = getCurrencyPrecision(storageCost);
  const { tooltipHeader, tooltipFooter } = useComputeSpendTooltip({
    showStorage,
    timezone,
    isAnywhereProvider: cluster.providerType === K8sProvider.ANYWHERE,
  });

  return (
    <ReportSection title="Compute spend" testId="compute-spend-section">
      <Box mt="12px">
        <TourStep
          step={tourSteps.computeSpendChart}
          type={costOverTimeTourId}
          text="Check the actual cluster compute spend in the selected period and cost distribution by resource offering over time."
          highlight="shadow"
          placement="top"
        >
          <Card sx={{ p: 0 }} className="tour-highlight">
            <Box
              display="flex"
              justifyContent="space-between"
              py="12px"
              px="16px"
            >
              <Box flex={1}>
                <LargeMetricsBox
                  testId="total-compute-spend"
                  title={
                    <Stack direction="row" alignItems="center" gap="4px">
                      {isDefaultFilter
                        ? dynamicCopiesMap.computeSpend.mainTitle
                        : 'Total compute spend'}
                      {isDefaultFilter && (
                        <InfoTooltip
                          title={dynamicCopiesMap.computeSpend.mainTooltip}
                          placement="top"
                          iconSize="14px"
                        />
                      )}
                    </Stack>
                  }
                  primaryValue={
                    isCollecting ? (
                      LOADING_MONEY
                    ) : (
                      <Money
                        amount={mainCost}
                        precision={mainCostPrecision}
                        fixed={mainCostPrecision}
                      />
                    )
                  }
                  primaryValueProps={{
                    variant: 'h5',
                    lineHeight: '28px',
                  }}
                />
              </Box>
              {cluster.providerType !== K8sProvider.ANYWHERE && (
                <Stack flex={1} direction="row" justifyContent="space-between">
                  <Box width="100%">
                    <LargeMetricsBox
                      testId="on-demand-compute-spend"
                      markerColor={
                        computeSpendColorsMap[ComputeSpendType.ON_DEMAND]
                      }
                      title="On-demand spend"
                      primaryValue={
                        isCollecting ? (
                          LOADING_MONEY
                        ) : (
                          <Money
                            amount={onDemandCost}
                            precision={onDemandCostPrecision}
                            fixed={onDemandCostPrecision}
                            dashesIfUndefined
                          />
                        )
                      }
                    />
                  </Box>

                  <Box width="100%">
                    <LargeMetricsBox
                      testId="fallback-compute-spend"
                      markerColor={
                        computeSpendColorsMap[ComputeSpendType.FALLBACK]
                      }
                      title={
                        <Stack direction="row" alignItems="center" gap="4px">
                          fallback spend
                          <InfoTooltip
                            title="Temporary on-demand node while spot is not available"
                            placement="bottom"
                          />
                        </Stack>
                      }
                      primaryValue={
                        isCollecting ? (
                          LOADING_MONEY
                        ) : (
                          <Money
                            amount={fallbackCost}
                            precision={fallbackCostPrecision}
                            fixed={fallbackCostPrecision}
                            dashesIfUndefined
                          />
                        )
                      }
                    />
                  </Box>
                  <Box width="100%">
                    <LargeMetricsBox
                      testId="spot-compute-spend"
                      markerColor={computeSpendColorsMap[ComputeSpendType.SPOT]}
                      title="spot spend"
                      primaryValue={
                        isCollecting ? (
                          LOADING_MONEY
                        ) : (
                          <Money
                            amount={spotCost}
                            precision={spotCostPrecision}
                            fixed={spotCostPrecision}
                            dashesIfUndefined
                          />
                        )
                      }
                    />
                  </Box>
                  {showStorage && (
                    <Box width="100%">
                      <LargeMetricsBox
                        testId="storage-compute-spend"
                        markerColor={
                          computeSpendColorsMap[ComputeSpendType.STORAGE]
                        }
                        title="storage spend"
                        primaryValue={
                          isCollecting ? (
                            LOADING_MONEY
                          ) : (
                            <Money
                              amount={storageCost}
                              precision={storageCostPrecision}
                              fixed={storageCostPrecision}
                              dashesIfUndefined
                            />
                          )
                        }
                      />
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
            <Box borderTop={tokens.theme.border.structural.neutralSubtle}>
              <WhenProvider isNot provider={K8sProvider.ANYWHERE}>
                <ComputeSpendControls showStorage={showStorage} />
              </WhenProvider>
              <ShadowDivider opacity={0.2} size={16} />
            </Box>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="L10B" pl="19px">
                {chartMode === ComputeSpendMode.DAILY_COST
                  ? 'Daily cost'
                  : 'Cumulative cost'}
              </Typography>
            </Stack>
            <CostReportChartWithAnomalies<ComputeSpendChartData>
              timezone={timezone}
              chartType={chartType}
              isLoading={isCollecting}
              tooltipHeader={tooltipHeader}
              tooltipFooter={tooltipFooter}
              hideSeriesInTooltip
              AnomalyTooltipComponent={(props) => (
                <ComputeSpendAnomalyTooltip {...props} timezone={timezone} />
              )}
              data={data?.length > 0 ? data : emptyData}
              height={240}
              chartSx={{ px: 16 }}
              config={config}
              yAxisProps={{
                tickFormatter: (tick: number) => `$${tick}`,
              }}
              testId="compute-spend-chart"
              buildPrompt={buildCostReportAnalyzePrompt({
                scopeName: cluster?.name,
                context: 'compute spend',
                getDefaultRange: getRange,
              })}
            />
          </Card>
        </TourStep>
      </Box>
    </ReportSection>
  );
};
